import { useMemo, useRef } from "react";

import InformationCircle from "@impero-com/common/img/information-circle.svg";
import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    Button,
    Svg,
    Tooltip,
    TST_COLUMN_SIZE,
    TSTTable,
} from "@impero-com/ui";
import {
    createColumnHelper,
    getCoreRowModel,
    useReactTable,
} from "@tanstack/react-table";

import { EntityView } from "api/generated/model";
import DeleteIcon from "img/delete.svg";

import * as theme from "./theme.css";

export interface TableEntity {
    entity: EntityView;
    scope: { type: "NODE" } | { type: "ANCESTOR"; path: string };
}

interface AssignEntitiesTableProps {
    entities: Array<TableEntity>;
    canDeleteEntityLink: boolean;
    // Entity added in the last edit; highlighted like the antd version did
    lastAddedEntityId: number | undefined;
    onConfirmUnassign: (entityId: number) => void;
}

export function AssignEntitiesTable({
    entities,
    canDeleteEntityLink,
    lastAddedEntityId,
    onConfirmUnassign,
}: AssignEntitiesTableProps) {
    const { t } = useTranslation();
    const columns = useMemo(
        () => buildColumns(t, canDeleteEntityLink, onConfirmUnassign),
        [t, canDeleteEntityLink, onConfirmUnassign]
    );
    const tableRef = useRef<HTMLDivElement>(null);

    const rowSelection =
        lastAddedEntityId !== undefined
            ? { [lastAddedEntityId.toString()]: true }
            : {};

    const table = useReactTable({
        data: entities,
        columns,
        state: { rowSelection },
        // The antd version rendered no header and no sorting; TSTTable always
        // renders a header, so disable sorting to avoid a dead sort affordance
        enableSorting: false,
        getRowId: (row) => row.entity.id.toString(),
        getCoreRowModel: getCoreRowModel<TableEntity>(),
    });

    // The antd version hid its empty placeholder; render nothing when empty
    if (entities.length === 0) {
        return null;
    }

    return (
        <TSTTable
            tableRef={tableRef}
            table={table}
            noResultsFallback={t("NoResults")}
            noSourceRowsFallback={t("NoResults")}
        />
    );
}

interface EntityActionsCellProps {
    entity: TableEntity;
    canDeleteEntityLink: boolean;
    onConfirmUnassign: (entityId: number) => void;
    t: TFunction;
}

function EntityActionsCell({
    entity,
    canDeleteEntityLink,
    onConfirmUnassign,
    t,
}: EntityActionsCellProps) {
    switch (entity.scope.type) {
        case "NODE": {
            if (!canDeleteEntityLink) {
                return null;
            }
            return (
                <div className={theme.actionContainer}>
                    <Button
                        id={"delete-entity-link-" + entity.entity.id.toString()}
                        variant="link"
                        className={theme.deleteButton}
                        onClick={() => onConfirmUnassign(entity.entity.id)}
                    >
                        <Svg src={DeleteIcon} className={theme.deleteIcon} />
                    </Button>
                </div>
            );
        }
        case "ANCESTOR": {
            const tooltip = `${t(
                "RiskDirectory_NodeUpdate_Entities_LinkedTo"
            )} ${entity.scope.path}`;
            return (
                <div className={theme.actionContainer}>
                    <Tooltip title={tooltip}>
                        <span>
                            <Svg
                                src={InformationCircle}
                                className={theme.infoIcon}
                            />
                        </span>
                    </Tooltip>
                </div>
            );
        }
        default: {
            return assertNever(entity.scope);
        }
    }
}

const columnHelper = createColumnHelper<TableEntity>();

function buildColumns(
    t: TFunction,
    canDeleteEntityLink: boolean,
    onConfirmUnassign: (entityId: number) => void
) {
    return [
        columnHelper.accessor((row) => row.entity.name, {
            id: "name",
            cell: (info) => {
                const entity = info.row.original;
                switch (entity.scope.type) {
                    case "NODE": {
                        return entity.entity.name;
                    }
                    case "ANCESTOR": {
                        return (
                            <span className={theme.ancestorEntity}>
                                {entity.entity.name}
                            </span>
                        );
                    }
                    default: {
                        return assertNever(entity.scope);
                    }
                }
            },
            size: TST_COLUMN_SIZE.L,
        }),
        columnHelper.display({
            id: "actions",
            size: TST_COLUMN_SIZE.CHECKBOX,
            meta: { fixedWidth: true },
            cell: (info) => (
                <EntityActionsCell
                    entity={info.row.original}
                    canDeleteEntityLink={canDeleteEntityLink}
                    onConfirmUnassign={onConfirmUnassign}
                    t={t}
                />
            ),
        }),
    ];
}
